import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  useAdministerMedicationMutation,
  useGetLocationStaffQuery,
} from '../../store/api/localApi';
import { MedicationEntryType } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { formatCivilDate, formatTime } from '../../utils/date';
import { useToast } from '../Toast';
import WitnessPicker from './WitnessPicker';

/** What the panel hands the modal. A slot subject is a scheduled dose; an
 *  emergency subject is an EMERGENCY_ONLY requirement being used in anger. */
export interface AdministerSubject {
  requirementId: string;
  medicationName: string;
  /** The requirement's dose — the modal opens with this prefilled. */
  dosage: string;
  childName?: string;
  /** "HH:MM" for a scheduled slot; absent for an emergency use. */
  slot?: string;
  /** EMERGENCY_ONLY requirement used in an emergency. Logs EMERGENCY_USE, and
   *  the server notifies guardians + location admins on a feature nobody can
   *  switch off. */
  isEmergency?: boolean;
  /** The medicine has passed its expiry (set to the expiry date). The server
   *  refuses a routine dose (MEDICATION_EXPIRED) but still records an
   *  emergency use — warn loudly, don't block (an expired EpiPen in
   *  anaphylaxis still beats nothing). */
  medicationExpiredOn?: string;
}

interface Props {
  subject: AdministerSubject;
  locationId: string;
  onClose: () => void;
}

/** The server's double-dose 409. Warn, don't block: a second dose in a slot is
 *  sometimes correct, and the override reason is what makes it auditable. */
const DOUBLE_DOSE_CODE = 'DOUBLE_DOSE_NEEDS_OVERRIDE';
/** The server's unwitnessed 400 — a fallback if the client mirror ever drifts. */
const WITNESS_OR_REASON_CODE = 'WITNESS_OR_REASON_REQUIRED';

const TEXT_LIMIT = 2000;

/**
 * The medicine-trolley modal: staff confirm a dose rather than typing one.
 *
 * THE INTERACTION BUDGET IS THE DESIGN. The witnessed path is exactly four
 * interactions and the e2e counts them:
 *   1. tap the due item (opens this modal)
 *   2. tap a witness
 *   3. tap "Confirm witness"
 *   4. tap "Log dose"
 *
 * Everything else is therefore PREFILLED and never blocks submit: the
 * medication and its dose come from the requirement, the time is now, and the
 * administrator is the signed-in user (the server takes the administrator from
 * the verified context regardless — it is displayed here so staff can see
 * whose name goes on the record, not so they can choose it).
 *
 * If you are adding a field to this modal: it must not be required on the
 * witnessed path, or it breaks the budget and the e2e fails. That failure is
 * the point.
 *
 * Two paths deliberately cost more, and are counted separately:
 *   - NO WITNESS costs a reason. That is the price of skipping the second
 *     signature, and the server enforces it (WITNESS_OR_REASON_REQUIRED).
 *   - A SECOND DOSE in the same slot costs an override reason, after the
 *     server has said so (409 DOUBLE_DOSE_NEEDS_OVERRIDE). The warning is
 *     surfaced, not pre-empted — the client does not attempt its own
 *     duplicate detection, because the log is the only source of truth about
 *     what has already been given.
 */
const AdministerModal: React.FC<Props> = ({ subject, locationId, onClose }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [administerMedication, { isLoading: isSaving }] = useAdministerMedicationMutation();
  const { data: staffData } = useGetLocationStaffQuery({ locationId });

  // PREFILLED on open and editable: the requirement's dose, and now.
  const [doseGiven, setDoseGiven] = useState(subject.dosage);
  const [administeredAt, setAdministeredAt] = useState(() => toLocalDateTimeInputValue(new Date()));

  const [witnessStaffId, setWitnessStaffId] = useState('');
  /** Set once the witness choice is settled — either a confirmed witness or a
   *  deliberate "no witness". Until then the submit button explains itself
   *  rather than firing a request the server will reject. */
  const [witnessSettled, setWitnessSettled] = useState(false);
  const [unwitnessed, setUnwitnessed] = useState(false);
  const [unwitnessedReason, setUnwitnessedReason] = useState('');
  const [notes, setNotes] = useState('');

  const [doubleDose, setDoubleDose] = useState<{ administeredByName: string; at: string } | null>(
    null
  );
  const [overrideReason, setOverrideReason] = useState('');
  /** EMERGENCY_USE was logged: staff are prompted to file an incident. No link
   *  is stored — incident linkage is v1.1. */
  const [emergencyLogged, setEmergencyLogged] = useState(false);

  // The administrator can never witness their own dose (the server rejects it
  // with WITNESS_IS_ADMINISTRATOR), so they are filtered out of the candidates
  // rather than offered and then refused.
  const witnessCandidates = (staffData?.staff ?? [])
    .filter((s) => s.id !== user?.id)
    .map((s) => ({ id: s.id, name: `${s.firstName} ${s.lastName}`.trim() }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const administratorName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'You';

  const handleSubmit = async () => {
    if (isSaving) return;

    // Client mirror of validate-witness.ts. Surfaced before the round trip so
    // the reason field appears instantly; the server's own rejection is still
    // handled below in case the two ever drift.
    if (unwitnessed && !unwitnessedReason.trim()) {
      showError('Say why this dose had no witness.');
      return;
    }
    if (!unwitnessed && !witnessStaffId) {
      showError('Pick a witness, or record that none was available.');
      return;
    }
    // A surfaced double-dose warning has to be answered before the retry.
    if (doubleDose && !overrideReason.trim()) {
      showError('Say why a second dose is being given in this slot.');
      return;
    }

    const isEmergency = subject.isEmergency === true;
    try {
      await administerMedication({
        medicationId: subject.requirementId,
        entryType: isEmergency
          ? MedicationEntryType.EMERGENCY_USE
          : MedicationEntryType.ADMINISTERED,
        administeredAt: new Date(administeredAt).toISOString(),
        doseGiven: doseGiven.trim() || undefined,
        ...(witnessStaffId ? { witnessStaffId } : {}),
        ...(unwitnessed && unwitnessedReason.trim()
          ? { unwitnessedReason: unwitnessedReason.trim() }
          : {}),
        // EMERGENCY_USE is a NOTES_REQUIRED_TYPES entry server-side, so an
        // emergency dose always carries a statement of what happened. The
        // default is honest — it says exactly what the record knows — and
        // stays editable in the notes field above.
        ...(notes.trim()
          ? { notes: notes.trim() }
          : isEmergency
          ? { notes: 'Emergency medication given as per the requirement instructions.' }
          : {}),
        ...(overrideReason.trim() ? { overrideReason: overrideReason.trim() } : {}),
      }).unwrap();

      if (isEmergency) {
        // Stay open on the incident prompt. Closing here would bury the one
        // instruction that matters after an emergency dose.
        setEmergencyLogged(true);
        return;
      }
      showSuccess(`${subject.medicationName} logged.`);
      onClose();
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (
              err as {
                data?: {
                  error?: string;
                  message?: string;
                  existingEntry?: { administeredByName?: string; administeredAt?: string };
                };
              }
            ).data
          : undefined;

      if (data?.error === DOUBLE_DOSE_CODE) {
        setDoubleDose({
          administeredByName: data.existingEntry?.administeredByName ?? 'another staff member',
          at: data.existingEntry?.administeredAt ?? '',
        });
        return;
      }
      if (data?.error === WITNESS_OR_REASON_CODE) {
        setUnwitnessed(true);
        setWitnessSettled(true);
        return;
      }
      showError(data?.error || data?.message || 'Could not log this dose.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="administer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="administer-modal-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
          data-testid="administer-backdrop"
          aria-hidden="true"
        />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 id="administer-modal-title" className="text-lg font-semibold text-gray-900">
                {subject.isEmergency ? 'Emergency medication' : 'Log dose'}
              </h3>
              <p className="text-sm text-gray-600" data-testid="administer-subject">
                {subject.medicationName}
                {subject.childName ? ` · ${subject.childName}` : ''}
                {subject.slot ? ` · ${subject.slot}` : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="administer-close-button"
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {subject.medicationExpiredOn && (
            <div
              data-testid="administer-expired-warning"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              <p className="font-semibold">
                This medication expired on {formatCivilDate(subject.medicationExpiredOn)}.
              </p>
              <p className="mt-0.5">
                {subject.isEmergency
                  ? 'An emergency use is still recorded — ask the guardian for a replacement.'
                  : 'Do not give a routine dose — the record will be refused. Ask the guardian for a replacement.'}
              </p>
            </div>
          )}

          {emergencyLogged ? (
            /* The emergency dose is already written. Everything else is gone —
               the only thing left is the instruction to file an incident. */
            <div
              data-testid="incident-prompt"
              className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-2"
            >
              <p className="text-sm font-semibold text-amber-900">
                Emergency dose logged. File an incident report now.
              </p>
              <p className="text-xs text-amber-800">
                Emergency medication given at the service is a reportable event. Record it as an
                incident from the child&apos;s row on this session before the end of your shift.
              </p>
              <button
                type="button"
                data-testid="incident-prompt-dismiss"
                onClick={onClose}
                className="px-3 py-2 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700"
              >
                Got it
              </button>
            </div>
          ) : (
            <>
              {/* Prefilled, editable, and never blocking: dose, time, and who
                  is signing. Staff confirm these — they do not fill them in. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="administer-dose"
                    className="block text-xs font-medium text-gray-700 mb-1"
                  >
                    Dose
                  </label>
                  <input
                    id="administer-dose"
                    type="text"
                    data-testid="administer-dose-input"
                    value={doseGiven}
                    onChange={(e) => setDoseGiven(e.target.value.slice(0, 200))}
                    className="block w-full rounded-md border-gray-300 text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="administer-at"
                    className="block text-xs font-medium text-gray-700 mb-1"
                  >
                    Time given
                  </label>
                  <input
                    id="administer-at"
                    type="datetime-local"
                    data-testid="administer-at-input"
                    value={administeredAt}
                    onChange={(e) => setAdministeredAt(e.target.value)}
                    className="block w-full rounded-md border-gray-300 text-sm"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500" data-testid="administer-administrator">
                Given by {administratorName}
              </p>

              {/* Witness: pick, then confirm. The confirm step is the third of
                  the four budgeted interactions and exists because a
                  mis-tapped witness name on a medication record is exactly the
                  error the second signature is meant to catch. */}
              {witnessSettled ? (
                <div
                  className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2"
                  data-testid="administer-witness-summary"
                >
                  {unwitnessed ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-800">No witness available.</p>
                      <label
                        htmlFor="unwitnessed-reason"
                        className="block text-xs font-medium text-gray-700"
                      >
                        Why was there no witness? <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="unwitnessed-reason"
                        data-testid="unwitnessed-reason-input"
                        value={unwitnessedReason}
                        onChange={(e) => setUnwitnessedReason(e.target.value.slice(0, TEXT_LIMIT))}
                        placeholder="e.g. Lone staff at 08:00"
                        className="block w-full rounded-md border-gray-300 text-sm h-16 resize-y"
                      />
                      <button
                        type="button"
                        data-testid="witness-reopen-button"
                        onClick={() => {
                          setUnwitnessed(false);
                          setWitnessSettled(false);
                        }}
                        className="text-xs text-primary-600 underline"
                      >
                        Pick a witness instead
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-gray-800">
                        Witnessed by{' '}
                        <span className="font-medium">
                          {witnessCandidates.find((c) => c.id === witnessStaffId)?.name ?? 'staff'}
                        </span>
                      </p>
                      <button
                        type="button"
                        data-testid="witness-reopen-button"
                        onClick={() => setWitnessSettled(false)}
                        className="text-xs text-primary-600 underline"
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <WitnessPicker
                    candidates={witnessCandidates}
                    value={witnessStaffId}
                    onPick={setWitnessStaffId}
                    onNoWitness={() => {
                      setWitnessStaffId('');
                      setUnwitnessed(true);
                      setWitnessSettled(true);
                    }}
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    data-testid="witness-confirm-button"
                    onClick={() => {
                      if (!witnessStaffId) {
                        showError('Pick a witness, or record that none was available.');
                        return;
                      }
                      setUnwitnessed(false);
                      setWitnessSettled(true);
                    }}
                    disabled={isSaving}
                    className="px-3 py-2 text-sm rounded-md bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
                  >
                    Confirm witness
                  </button>
                </div>
              )}

              {/* Surfaced only after the server's 409 — the client never tries
                  to guess whether a dose is a duplicate. */}
              {doubleDose && (
                <div
                  data-testid="double-dose-warning"
                  className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2"
                >
                  <p className="text-sm font-semibold text-amber-900">
                    This dose has already been given today.
                  </p>
                  <p className="text-xs text-amber-800">
                    Logged by {doubleDose.administeredByName}
                    {doubleDose.at ? ` at ${formatTime(doubleDose.at)}` : ''}. You can still record
                    a second dose, but you must say why.
                  </p>
                  <label
                    htmlFor="override-reason"
                    className="block text-xs font-medium text-amber-900"
                  >
                    Why a second dose? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="override-reason"
                    data-testid="override-reason-input"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value.slice(0, TEXT_LIMIT))}
                    placeholder="e.g. First dose was vomited back within a minute"
                    className="block w-full rounded-md border-amber-300 text-sm h-16 resize-y"
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="administer-notes"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Notes
                </label>
                <textarea
                  id="administer-notes"
                  data-testid="administer-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, TEXT_LIMIT))}
                  placeholder={
                    subject.isEmergency
                      ? 'What happened, and what was done…'
                      : 'Anything worth recording…'
                  }
                  className="block w-full rounded-md border-gray-300 text-sm h-16 resize-y"
                />
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="administer-cancel-button"
                  className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="administer-submit-button"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isSaving ? 'Logging…' : subject.isEmergency ? 'Log emergency use' : 'Log dose'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** `datetime-local` needs "YYYY-MM-DDTHH:mm" in LOCAL time — `toISOString()`
 *  would silently shift the prefilled time by the browser's UTC offset. */
function toLocalDateTimeInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export default AdministerModal;
